// Lucide icons by name, sized to the text (1.2em) and colored by `currentColor`.
// Names are kebab-case like lucide.dev, so data (skills, nav) can refer to icons as plain strings.
// To add an icon: import it from lucide-react and add it to ICONS.
import {
  ArrowLeftRight,
  ArrowRight,
  AudioLines,
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  ChartColumn,
  ChartLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CirclePlay,
  Clock,
  Coins,
  CornerDownRight,
  createLucideIcon,
  Download,
  Dumbbell,
  Ear,
  Ellipsis,
  EllipsisVertical,
  Eye,
  Film,
  Flame,
  FlaskConical,
  Gamepad2,
  GraduationCap,
  Hand,
  Headphones,
  HeartPulse,
  House,
  Info,
  Keyboard,
  Layers,
  Lightbulb,
  ListOrdered,
  Lock,
  LogOut,
  type LucideIcon,
  MessageCircle,
  MessagesSquare,
  Mic,
  Minus,
  Monitor,
  MonitorDown,
  Moon,
  Music,
  Newspaper,
  Pause,
  PenLine,
  Plane,
  Play,
  Plus,
  Repeat,
  RotateCcw,
  Search,
  Send,
  Settings,
  Share,
  Shuffle,
  Smartphone,
  Sparkles,
  Speech,
  SpellCheck,
  Square,
  Star,
  Sun,
  Target,
  TextCursorInput,
  Timer,
  Trash2,
  TrendingDown,
  TrendingUp,
  Trophy,
  Upload,
  User,
  Users,
  Utensils,
  Video,
  Volume2,
  X,
  Zap,
} from 'lucide-react';

/** Five voice bars: the Practice tab and anything that means "your voice". The logo is BrandMark. */
const Voice = createLucideIcon('voice', [
  ['path', { d: 'M4.5 8.25v7.5M8.25 5.25v13.5M12 3v18M15.75 7.5v9M19.5 5.25v13.5', key: 'bars' }],
]);

/** Safari's page menu button on iPhone (iOS 27): three lines, the last one shorter. */
const PageMenu = createLucideIcon('page-menu', [['path', { d: 'M4 6h16M4 12h16M4 18h10', key: 'lines' }]]);

const ICONS = {
  'arrow-left-right': ArrowLeftRight,
  'arrow-right': ArrowRight,
  'audio-lines': AudioLines,
  book: BookOpen,
  calendar: Calendar,
  'chart-column': ChartColumn,
  'chart-line': ChartLine,
  check: Check,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  alert: CircleAlert,
  'circle-check': CircleCheck,
  'circle-play': CirclePlay,
  clock: Clock,
  coins: Coins,
  'corner-down-right': CornerDownRight,
  download: Download,
  dumbbell: Dumbbell,
  ear: Ear,
  more: Ellipsis,
  'more-vertical': EllipsisVertical,
  eye: Eye,
  flame: Flame,
  'gamepad-2': Gamepad2,
  hand: Hand,
  headphones: Headphones,
  house: House,
  info: Info,
  keyboard: Keyboard,
  layers: Layers,
  bulb: Lightbulb,
  'list-ordered': ListOrdered,
  lock: Lock,
  'log-out': LogOut,
  'messages-square': MessagesSquare,
  mic: Mic,
  minus: Minus,
  monitor: Monitor,
  'monitor-down': MonitorDown,
  moon: Moon,
  pause: Pause,
  'pen-line': PenLine,
  play: Play,
  plus: Plus,
  repeat: Repeat,
  'rotate-ccw': RotateCcw,
  send: Send,
  'page-menu': PageMenu,
  settings: Settings,
  share: Share,
  shuffle: Shuffle,
  sparkles: Sparkles,
  'spell-check': SpellCheck,
  speech: Speech,
  square: Square,
  star: Star,
  sun: Sun,
  target: Target,
  'text-cursor-input': TextCursorInput,
  timer: Timer,
  'trash-2': Trash2,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  upload: Upload,
  user: User,
  video: Video,
  voice: Voice,
  'volume-2': Volume2,
  x: X,
  briefcase: Briefcase,
  building: Building2,
  film: Film,
  flask: FlaskConical,
  'graduation-cap': GraduationCap,
  'heart-pulse': HeartPulse,
  'message-circle': MessageCircle,
  music: Music,
  newspaper: Newspaper,
  plane: Plane,
  search: Search,
  smartphone: Smartphone,
  trophy: Trophy,
  users: Users,
  utensils: Utensils,
  zap: Zap,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICONS;

type IconProps = { name: IconName; className?: string | undefined; label?: string };

/** Decorative unless `label` is given, in which case screen readers announce it. */
export function Icon({ name, className, label }: IconProps) {
  const Svg = ICONS[name];
  return (
    <Svg
      className={className ? `ic ${className}` : 'ic'}
      strokeWidth={2}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
    />
  );
}
